import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Trash, BookOpen, Wrench, Receipt, Undo2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

export default function TrashPage() {
  const t = useT();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [working, setWorking] = useState(null);

  const reload = async () => {
    setLoading(true);
    const [l, m, i] = await Promise.all([
      api.get("/daily-logs/trash").catch(() => ({ data: [] })),
      api.get("/maintenance/trash").catch(() => ({ data: [] })),
      api.get("/invoices/trash").catch(() => ({ data: [] })),
    ]);
    setLogs(l.data); setTickets(m.data); setInvoices(i.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const action = async (kind, id, op) => {
    setWorking(id);
    try {
      if (op === "restore") {
        await api.post(`/${kind}/${id}/restore`);
        toast.success(t("trash.restoredToast"));
      } else if (op === "purge") {
        if (!window.confirm(t("trash.purgeConfirm"))) { setWorking(null); return; }
        await api.delete(`/${kind}/${id}/purge`);
        toast.success(t("trash.purgedToast"));
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setWorking(null);
    }
  };

  const total = logs.length + tickets.length + invoices.length;

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#FBBF24]">// {t("trash.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("trash.title")}</h2>
        <p className="text-sm text-[#94A3B8] mt-1">{t("trash.intro")}</p>
      </div>

      {loading ? (
        <div className="ledger-card p-8 text-center text-[#94A3B8]"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />{t("common.loading")}</div>
      ) : total === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]" data-testid="trash-empty">
          <Trash className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">{t("trash.empty")}</div>
        </div>
      ) : (
        <>
          <Section
            icon={BookOpen} title={t("trash.section.logs")} items={logs} kind="daily-logs"
            isAdmin={isAdmin} working={working} action={action} t={t}
            renderItem={(x) => (
              <>
                <div className="text-sm font-medium truncate">{x.place_name || x.route || t("journal.untitled")}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{t(`journal.logType.${x.log_type}`)} · {fmtDate(x.log_date)}</div>
              </>
            )}
          />
          <Section
            icon={Wrench} title={t("trash.section.tickets")} items={tickets} kind="maintenance"
            isAdmin={isAdmin} working={working} action={action} t={t}
            renderItem={(x) => (
              <>
                <div className="text-sm font-medium truncate">{x.title}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{x.status} · {x.severity} · {fmtDate(x.opened_at?.slice(0, 10))}</div>
              </>
            )}
          />
          <Section
            icon={Receipt} title={t("trash.section.invoices")} items={invoices} kind="invoices"
            isAdmin={isAdmin} working={working} action={action} t={t}
            renderItem={(x) => (
              <>
                <div className="text-sm font-medium truncate">{x.vendor || "—"} · {x.invoice_number || "—"}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{x.amount} {x.currency || "EUR"} · {fmtDate(x.invoice_date)}</div>
              </>
            )}
          />
        </>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, items, kind, isAdmin, working, action, t, renderItem }) {
  if (!items.length) return null;
  return (
    <div className="ledger-card p-5" data-testid={`trash-${kind}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#FBBF24]" />
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{title} · {items.length}</div>
      </div>
      <div className="space-y-2">
        {items.map(x => (
          <div key={x.id} className="flex items-center justify-between gap-3 p-3 bg-[#050A14] border border-[#1E293B] rounded-md" data-testid={`trash-${kind}-${x.id}`}>
            <div className="min-w-0 flex-1">{renderItem(x)}</div>
            <div className="hidden md:block text-right shrink-0">
              <div className="font-mono text-[9px] text-[#94A3B8]">{t("trash.purgesOn")}</div>
              <div className="font-mono text-[10px] text-[#FBBF24]">{fmtDate(x.purge_after)}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => action(kind, x.id, "restore")}
                disabled={working === x.id}
                data-testid={`trash-restore-${kind}-${x.id}`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs bg-[#10B981]/15 border border-[#10B981]/40 text-[#34D399] hover:bg-[#10B981]/25 disabled:opacity-50">
                {working === x.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                <span className="hidden sm:inline">{t("trash.restore")}</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => action(kind, x.id, "purge")}
                  disabled={working === x.id}
                  data-testid={`trash-purge-${kind}-${x.id}`}
                  title={t("trash.purgeNow")}
                  className="p-1.5 rounded-md bg-[#050A14] border border-[#EF4444]/30 text-[#F87171] hover:bg-[#7F1D1D] disabled:opacity-50">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
